// File: src/English/Grade9/Grade9EnglishLesson.js

import React, { useState, useEffect, useRef } from "react";
import { useNavigate, Link } from "react-router-dom";
import "./Grade9EnglishLesson.css";

import { awardToken } from "../../awardtoken.js";
import { getNounsPool } from "../EnglishTool/pool/nounsPool.js";
import { getVerbsPool } from "../EnglishTool/pool/verbsPool.js";
import { getAdjectivesPool } from "../EnglishTool/pool/adjectivesPool.js";
import { getAdverbsPool } from "../EnglishTool/pool/adverbsPool.js";
import { getReadingPool } from "../EnglishTool/pool/readingPool.js";
import { shuffleArray } from "../EnglishTool/shuffleArray.js";
import { getSentencePool } from "../EnglishTool/pool/sentencePool.js";

//Pool
import { literaryDevicesPool } from "../EnglishTool/pool/literaryDevicesPool.js";
import { sentenceTypesPool } from "../EnglishTool/pool/sentenceTypesPool.js";
import { textStructurePool } from "../EnglishTool/pool/textStructurePool.js";
import { connotationPool } from "../EnglishTool/pool/connotationPool.js";
import { mediaDevicesPool } from "../EnglishTool/pool/mediaDevicesPool.js";
import {
  allAdvancedNounQuestions
} from "../EnglishTool/advancedNounQuestions";
import { allAdvancedVerbQuestions } from "../EnglishTool/advancedVerbQuestions";
import { allAdvancedAdjectiveQuestions } from "../EnglishTool/advancedAdjectiveQuestions";
import { allAdvancedAdverbQuestions } from "../EnglishTool/advancedAdverbQuestions";

// Import shared quiz UI components
import {
  StoryModeMultipleChoice,  StoryModeFillInBlank,  StoryModeIdentify,  StoryModeMultiPart,  FinalQuiz} from "../EnglishTool/StoryModeStructure";

import { buildLiteraryDeviceMC, buildSentenceTypeMC, buildTextStructureMC, buildConnotationMC, buildMediaDeviceMC,} from "../EnglishTool/AdvanceEnglishHelper";
import { checkRequiredWord, checkSentenceStructure, makeSampleSentence } from "../EnglishTool/quizHelpers.js";
import { chapterPages } from "./Grade9chapterPage.js";
  
function getTopicForChapter(ch) {
  switch (ch) {
    case 1: return "nouns";
    case 2: return "verbs";
    case 3: return "adjectives";
    case 4: return "adverbs";
    default: return "nouns";
  }
}

// Ready for extension: add advanced pool usage per topic/grade as you build content.
export function generateChapterQuiz(topic, grade, n = 10) {
    // Nouns Quiz
    if (topic === "nouns") {
      let nounPool = getNounsPool(grade);
      let advancedNounQs = allAdvancedNounQuestions.map(f => f());
  
      let questions = [
        ...advancedNounQs,
        ...nounPool.map(entry => ({
          questionType: "MC",
          question: `Which of the following is a ${entry.nounType} noun?`,
          options: shuffleArray([
            entry.word,
            ...nounPool
              .filter(e => e.nounType !== entry.nounType)
              .map(e => e.word)
              .slice(0, 3)
          ]),
          correctAnswer: entry.word,
        })),
        ...getSentencePool(grade)
          .filter(s => s.focus === "nouns")
          .map(s => ({
            questionType: "IDENTIFY",
            question: `Identify all nouns in the sentence: "${s.sentence}"`,
            correctAnswers: s.correctAnswers,
          })),
        ...getReadingPool(grade).map(r => ({
          questionType: "MC",
          question: `What is the main idea of this passage? "${r.statement}"`,
          options: shuffleArray([
            r.mainIdea,
            ...getReadingPool(grade)
              .filter(x => x !== r)
              .slice(0, 3)
              .map(x => x.mainIdea),
          ]),
          correctAnswer: r.mainIdea,
        })),
        buildLiteraryDeviceMC(literaryDevicesPool),
        buildTextStructureMC(textStructurePool),
        buildConnotationMC(connotationPool),
        buildSentenceTypeMC(sentenceTypesPool),
      ].filter(Boolean);
  
      return shuffleArray(questions).slice(0, n);
    }
  
    // Verbs Quiz
    if (topic === "verbs") {
      let verbPool = getVerbsPool(grade);
      let advancedVerbQs = allAdvancedVerbQuestions.map(f => f());
  
      let questions = [
        ...advancedVerbQs,
        ...verbPool.map(word => ({
          questionType: "MC",
          question: `Which of the following is a verb?`,
          options: shuffleArray([
            word,
            ...getAdjectivesPool(grade).slice(0, 2),
            ...getNounsPool(grade).map(n => n.word).slice(0, 1)
          ]),
          correctAnswer: word,
        })),
        ...getSentencePool(grade)
          .filter(s => s.focus === "verbs")
          .map(s => ({
            questionType: "IDENTIFY",
            question: `Identify all verbs in the sentence: "${s.sentence}"`,
            correctAnswers: s.correctAnswers,
          })),
        ...getReadingPool(grade).map(r => ({
          questionType: "MC",
          question: `Which verb best describes the main action in: "${r.statement}"?`,
          options: shuffleArray([
            ...(r.statement.match(/\b\w+ing\b|\b\w+ed\b|\b\w+s\b/g) || []),
            "run", "make", "see", "think"
          ]).slice(0, 4),
          correctAnswer:
            (r.statement.match(/\b\w+ing\b|\b\w+ed\b|\b\w+s\b/g) || [])[0] || "",
        })),
        buildSentenceTypeMC(sentenceTypesPool),
        buildTextStructureMC(textStructurePool),
        buildLiteraryDeviceMC(literaryDevicesPool),
      ].filter(Boolean);
  
      return shuffleArray(questions).slice(0, n);
    }
  
    // Adjectives Quiz
    if (topic === "adjectives") {
      let adjPool = getAdjectivesPool(grade);
      let advancedAdjQs = allAdvancedAdjectiveQuestions.map(f => f());
  
      let questions = [
        ...advancedAdjQs,
        ...adjPool.map(word => ({
          questionType: "MC",
          question: `Which of the following is an adjective?`,
          options: shuffleArray([
            word,
            ...getVerbsPool(grade).slice(0, 2),
            ...getNounsPool(grade).map(n => n.word).slice(0, 1)
          ]),
          correctAnswer: word,
        })),
        ...getSentencePool(grade)
          .filter(s => s.focus === "adjectives")
          .map(s => ({
            questionType: "IDENTIFY",
            question: `Identify all adjectives in the sentence: "${s.sentence}"`,
            correctAnswers: s.correctAnswers,
          })),
        ...getReadingPool(grade).map(r => ({
          questionType: "MC",
          question: `Which adjective best describes the mood of: "${r.statement}"?`,
          options: shuffleArray([
            "happy", "serious", "hopeful", "calm", "busy", "uncertain"
          ]),
          correctAnswer: "calm" // placeholder; can add more logic later
        })),
        buildConnotationMC(connotationPool),
        buildTextStructureMC(textStructurePool),
      ].filter(Boolean);
  
      return shuffleArray(questions).slice(0, n);
    }
  
    // Adverbs Quiz
    if (topic === "adverbs") {
      let advPool = getAdverbsPool(grade);
      let advancedAdvQs = allAdvancedAdverbQuestions.map(f => f());
  
      let questions = [
        ...advancedAdvQs,
        ...advPool.map(word => ({
          questionType: "MC",
          question: `Which of the following is an adverb?`,
          options: shuffleArray([
            word,
            ...getAdjectivesPool(grade).slice(0, 2),
            ...getVerbsPool(grade).slice(0, 1)
          ]),
          correctAnswer: word,
        })),
        ...getSentencePool(grade)
          .filter(s => s.focus === "adverbs")
          .map(s => ({
            questionType: "IDENTIFY",
            question: `Identify all adverbs in the sentence: "${s.sentence}"`,
            correctAnswers: s.correctAnswers,
          })),
        ...getReadingPool(grade).map(r => ({
          questionType: "MC",
          question: `Which adverb could best describe how the subject acts in: "${r.statement}"?`,
          options: shuffleArray([
            "quickly", "carefully", "often", "loudly", "silently", "eagerly"
          ]),
          correctAnswer: "carefully" // placeholder
        })),
        buildMediaDeviceMC(mediaDevicesPool),
        buildTextStructureMC(textStructurePool),
        buildConnotationMC(connotationPool),
      ].filter(Boolean);
  
      return shuffleArray(questions).slice(0, n);
    }
  
    return [];
  }

  /** ================== MAIN COMPONENT ================== **/
export default function Grade9EnglishLesson({ onAwardTokens }) {
  const grade = 9;
  const [chapter, setChapter] = useState(1);
  const [pageIdx, setPageIdx] = useState(0);
  const [chapDone, setChapDone] = useState(false);

  const [showQuiz, setShowQuiz] = useState(false);
  const [quiz, setQuiz] = useState([]);
  const [qIdx, setQIdx] = useState(0);
  const [qScore, setQScore] = useState(0);
  const [quizFin, setQuizFin] = useState(false);

  const [showFinal, setShowFinal] = useState(false);
  const [finalQs, setFinalQs] = useState([]);
  const [fIdx, setFIdx] = useState(0);
  const [fScore, setFScore] = useState(0);
  const [fFin, setFFin] = useState(false);
  const [timeLeft, setTimeLeft] = useState(360);
  const hasAwardedTokens = useRef(false);

  const [lastFeedback, setLastFeedback] = useState(null);
  const [hasCompletedBefore, setHasCompletedBefore] = useState(false);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const savedUser = JSON.parse(localStorage.getItem("loggedInUser"));
    if (savedUser) setUser(savedUser);

    const storedChap = localStorage.getItem("g9English_chapter");
    const storedPage = localStorage.getItem("g9English_pageIdx");
    const done = localStorage.getItem("grade9EnglishComplete");
    if (storedChap) setChapter(parseInt(storedChap, 10) || 1);
    if (storedPage) setPageIdx(parseInt(storedPage, 10) || 0);
    if (done === "true") {
      setHasCompletedBefore(true);
      setChapDone(true);
    }
  }, []);

  useEffect(() => {
    localStorage.setItem("g9English_chapter", String(chapter));
    localStorage.setItem("g9English_pageIdx", String(pageIdx));
  }, [chapter, pageIdx]);

  useEffect(() => {
    if (chapDone) {
      localStorage.setItem("grade9EnglishComplete", "true");
    }
  }, [chapDone]);

  useEffect(() => {
    if (showFinal && !fFin && timeLeft > 0) {
      const timer = setTimeout(() => setTimeLeft(t => t - 1), 1000);
      return () => clearTimeout(timer);
    } else if (timeLeft === 0) {
      setFFin(true);
    }
  }, [showFinal, fFin, timeLeft]);

  function getTotalPages(obj) {
    return Object.values(obj).reduce((sum, arr) => sum + arr.length, 0);
  }
  function getAbsolutePage(obj, ch, pIdx) {
    let pagesBefore = 0;
    for (let c = 1; c < ch; c++) {
      pagesBefore += obj[c].length;
    }
    return pagesBefore + pIdx + 1;
  }
  const totalPages = getTotalPages(chapterPages);
  const absolutePage = getAbsolutePage(chapterPages, chapter, pageIdx);
  const progress = Math.round((absolutePage / totalPages) * 100);

  function startChapterQuiz() {
    const topic = getTopicForChapter(chapter);
    const generated = generateChapterQuiz(topic, grade, 9);
    setQuiz(generated);
    setQIdx(0);
    setQScore(0);
    setQuizFin(false);
    setShowQuiz(true);
  }

  function handleAnswer(opt) {
    const cQ = quiz[qIdx];
  
    // --- 1. Special logic for MC_MULTI (speaker/mood etc) ---
    if (cQ.questionType === "MC_MULTI") {
      // Compare speaker and mood (or whatever keys are relevant)
      if (
        opt &&
        cQ.correctAnswer &&
        opt.speaker === cQ.correctAnswer.speaker &&
        opt.mood === cQ.correctAnswer.mood
      ) {
        setQScore(s => s + 1);
        setLastFeedback({
          isCorrect: true,
          message: "Correct!",
          correctAnswer: `${cQ.correctAnswer.speaker}, ${cQ.correctAnswer.mood}`
        });
        setTimeout(() => {
          setLastFeedback(null);
          if (qIdx < quiz.length - 1) {
            setQIdx(i => i + 1);
          } else {
            setQuizFin(true);
          }
        }, 600);
      } else {
        setLastFeedback({
          isCorrect: false,
          message: `Not quite. The correct answer was: ${cQ.correctAnswer.speaker}, ${cQ.correctAnswer.mood}`,
          correctAnswer: `${cQ.correctAnswer.speaker}, ${cQ.correctAnswer.mood}`
        });
      }
      return;
    }
  
    // --- 2. Standard MC (string) logic ---
    if (opt === cQ.correctAnswer) {
      setQScore(s => s + 1);
      setLastFeedback({ isCorrect: true, message: "Correct!", correctAnswer: cQ.correctAnswer });
      setTimeout(() => {
        setLastFeedback(null);
        if (qIdx < quiz.length - 1) {
          setQIdx(i => i + 1);
        } else {
          setQuizFin(true);
        }
      }, 600);
    } else {
      setLastFeedback({
        isCorrect: false,
        message: `Not quite. The correct answer was: "${cQ.correctAnswer}".`,
        correctAnswer: cQ.correctAnswer
      });
    }
  }
  
  function handleFillInBlank(userInput) {
    const { checkType, requiredWord, question } = quiz[qIdx];
    let errors = [];
    let isCorrect = false;
    const isVerbQ = /verb/i.test(question);
    if (!checkRequiredWord(userInput, requiredWord, isVerbQ)) {
      errors.push(`You must use the word "${requiredWord}"${isVerbQ ? " (any correct form is OK)" : ""} in your sentence.`);
    }
    errors = errors.concat(checkSentenceStructure(userInput));
    isCorrect = errors.length === 0;
    setLastFeedback({
      userAnswer: userInput,
      isCorrect,
      errors,
      sample: makeSampleSentence(requiredWord, isVerbQ ? "verb" : checkType?.includes("adj") ? "adjective" : checkType?.includes("adv") ? "adverb" : "noun"),
    });
    if (isCorrect) {
      setQScore(s => s + 1);
      setTimeout(() => {
        setLastFeedback(null);
        if (qIdx < quiz.length - 1) {
          setQIdx(i => i + 1);
        } else {
          setQuizFin(true);
        }
      }, 800);
    }
  }

  function handleIdentifyWords(userInput) {
    const cQ = quiz[qIdx];
    const typed = userInput.split(",").map(s => s.trim().toLowerCase());
    const correctArr = (cQ.correctAnswers || []).map(w => w.toLowerCase());
    const correctFound = typed.filter(w => correctArr.includes(w));
    const missed = correctArr.filter(w => !typed.includes(w));
    const extra = typed.filter(w => !correctArr.includes(w));
    const isCorrect = correctFound.length === correctArr.length && extra.length === 0;
    setLastFeedback({
      isCorrect,
      correctFound,
      missed,
      extra,
      correctAnswers: cQ.correctAnswers,
      message: isCorrect
        ? "Great job! You found all the correct words."
        : [
            missed.length > 0 ? `Missing: ${missed.join(", ")}` : null,
            extra.length > 0 ? `Extra (incorrect): ${extra.join(", ")}` : null,
          ].filter(Boolean).join(" | ")
    });
    if (isCorrect) {
      setQScore(s => s + 1);
      setTimeout(() => {
        setLastFeedback(null);
        if (qIdx < quiz.length - 1) {
          setQIdx(i => i + 1);
        } else {
          setQuizFin(true);
        }
      }, 900);
    }
  }

  function handleMCQuizFinish() {
    if (qScore >= quiz.length / 2) {
      if (chapter < 5) {
        setChapter(ch => ch + 1);
        setPageIdx(0);
      } else {
        setChapDone(true);
      }
    } else {
      alert("Below 50%. Please review and retake!");
    }
    setQuiz([]);
    setQIdx(0);
    setQScore(0);
    setQuizFin(false);
    setShowQuiz(false);
  }

  function startFinalQuiz() {
    const final = generateFinalQuiz(grade, 45); // or however many you want
    setFinalQs(final);
    setFIdx(0);
    setFScore(0);
    setFFin(false);
    setTimeLeft(360);
    setShowFinal(true);
  }

  function handleFinalAnswer(opt) {
    const cQ = finalQs[fIdx];
    if (!cQ) return;
    if (cQ.questionType === "MC_MULTI") {
      // Expect answer like: { speaker: "X", mood: "Y" }
      if (
        opt &&
        opt.speaker &&
        opt.mood &&
        opt.speaker === cQ.correctAnswer.speaker &&
        opt.mood === cQ.correctAnswer.mood
      ) {
        setFScore(s => s + 1);
      }
    } else if (cQ.questionType === "IDENTIFY") {
      if (isIdentifyCorrect(opt, cQ.correctAnswers)) setFScore(s => s + 1);
    } else if (cQ.questionType === "FITB") {
      const isVerbQ = /verb/i.test(cQ.question);
      const keywordOk = checkRequiredWord(opt, cQ.requiredWord, isVerbQ);
      const errors = checkSentenceStructure(opt);
      if (keywordOk && errors.length === 0) setFScore(s => s + 1);
    } else {
      if (opt === cQ.correctAnswer) setFScore(s => s + 1);
    }
    if (fIdx < finalQs.length - 1) setFIdx(i => i + 1);
    else setFFin(true);
  }
  
  
  // Don't forget isIdentifyCorrect utility:
  function isIdentifyCorrect(userInput, correctAnswers) {
    if (!userInput || !correctAnswers) return false;
    const typed = userInput.split(",").map(s => s.trim().toLowerCase()).filter(Boolean);
    const correctArr = correctAnswers.map(w => w.toLowerCase()).filter(Boolean);
    if (typed.length !== correctArr.length) return false;
    return typed.slice().sort().every((val, idx) => val === correctArr.slice().sort()[idx]);
  }

  
  const navigate = useNavigate();

  useEffect(() => {
  if (fFin && !hasAwardedTokens.current) {
    const percent = Math.round((fScore / (finalQs.length || 1)) * 100);
    if (percent >= 80) {
      alert("You earned a token!");
      awardToken(); // <-- Actually give the token!
      if (typeof onAwardTokens === "function") {
        onAwardTokens(1);
      }
    }

    // --- Save final quiz result for dashboard ---
    const quizResults = JSON.parse(localStorage.getItem("quizResults") || "{}");
    quizResults["english_g9"] = {
      ...(quizResults["english_g9"] || {}),
      finalQuizScore: fScore,
      finalQuizTotal: finalQs.length,
      lastCompleted: Date.now(),
    };
    localStorage.setItem("quizResults", JSON.stringify(quizResults));
    // -------------------------------------------

    setTimeout(() => {
      navigate("/english");
    }, 500);

    hasAwardedTokens.current = true;
  }
}, [fFin, fScore, finalQs.length, onAwardTokens, navigate]);

  function ChapterSlide() {
    const currentPages = chapterPages[chapter];
    const page = currentPages[pageIdx];
    const isLastPage = pageIdx === currentPages.length - 1;
    return (
      <div>
        <h2>{page.title}</h2>
        <div>{page.body}</div>
        <div style={{ marginTop: 10 }}>
          {pageIdx > 0 && (
            <button className="button1" onClick={() => setPageIdx(p => p - 1)}>
              Back
            </button>
          )}
          {!isLastPage && (
            <button
              className="button1"
              onClick={() => setPageIdx(p => p + 1)}
              style={{ marginLeft: 10 }}
            >
              Next
            </button>
          )}
          {isLastPage && (
            <button
              className="button1"
              onClick={startChapterQuiz}
              style={{ marginLeft: 15 }}
            >
              Take Chapter {chapter} Quiz
            </button>
          )}
        </div>
      </div>
    );
  }

  function ChapterQuiz() {
    // Defensive: If the quiz is finished, only show the results, not any question.
    if (quizFin) {
      return (
        <div className="quiz-result-screen">
          <h3>Chapter {chapter} Quiz Finished!</h3>
          <p>You scored {qScore} / {quiz.length}.</p>
          <button className="button1" onClick={handleMCQuizFinish}>
            Continue
          </button>
        </div>
      );
    }
  
    // Prevent out-of-bounds errors (e.g. after finishing, qIdx might be past quiz.length - 1)
    if (!quiz[qIdx]) return null;
  
    const cQ = quiz[qIdx];
  
    switch (cQ.questionType) {
      case "MC":
        return (
          <StoryModeMultipleChoice
            cQ={cQ}
            onAnswerClick={handleAnswer}
            currentScore={qScore}
            quizLength={quiz.length}
            user={user}
            lastFeedback={lastFeedback}
            setLastFeedback={setLastFeedback}
          />
        );
      case "FITB":
        return (
          <StoryModeFillInBlank
            cQ={cQ}
            onSubmit={handleFillInBlank}
            currentScore={qScore}
            quizLength={quiz.length}
            user={user}
            lastFeedback={lastFeedback}
            setLastFeedback={setLastFeedback}
          />
        );
      case "IDENTIFY":
        return (
          <StoryModeIdentify
            cQ={cQ}
            onSubmit={handleIdentifyWords}
            currentScore={qScore}
            quizLength={quiz.length}
            user={user}
            lastFeedback={lastFeedback}
            setLastFeedback={setLastFeedback}
          />
        );
        case "MC_MULTI":
  return (
    <StoryModeMultiPart
      cQ={cQ}
      onAnswerClick={handleAnswer}
      currentScore={qScore}
      quizLength={quiz.length}
      user={user}
      lastFeedback={lastFeedback}
      setLastFeedback={setLastFeedback}
    />
  );
      default:
        return <p>Unknown question type</p>;
    }
  }
  

  function ChapterUI() {
    if (!showQuiz) {
      return <ChapterSlide />;
    } else {
      return <ChapterQuiz />;
    }
  }

  function buildFullQuestionBank(grade) {
    // Advanced question pools for deeper skills
    const advancedNounQs = allAdvancedNounQuestions.map(f => f());
    const advancedVerbQs = allAdvancedVerbQuestions.map(f => f());
    const advancedAdjQs = allAdvancedAdjectiveQuestions.map(f => f());
    const advancedAdvQs = allAdvancedAdverbQuestions.map(f => f());
  
    // MC/ID base questions from word pools
    const nounPool = getNounsPool(grade).map(entry => ({
      questionType: "MC",
      question: `Which of the following is a ${entry.nounType} noun?`,
      options: shuffleArray([
        entry.word,
        ...getNounsPool(grade)
          .filter(e => e.nounType !== entry.nounType)
          .map(e => e.word)
          .slice(0, 3)
      ]),
      correctAnswer: entry.word,
    }));
  
    const verbPool = getVerbsPool(grade).map(word => ({
      questionType: "MC",
      question: `Which of the following is a verb?`,
      options: shuffleArray([
        word,
        ...getAdjectivesPool(grade).slice(0, 2),
        ...getNounsPool(grade).map(n => n.word).slice(0, 1)
      ]),
      correctAnswer: word,
    }));
  
    const adjPool = getAdjectivesPool(grade).map(word => ({
      questionType: "MC",
      question: `Which of the following is an adjective?`,
      options: shuffleArray([
        word,
        ...getVerbsPool(grade).slice(0, 2),
        ...getNounsPool(grade).map(n => n.word).slice(0, 1)
      ]),
      correctAnswer: word,
    }));
  
    const advPool = getAdverbsPool(grade).map(word => ({
      questionType: "MC",
      question: `Which of the following is an adverb?`,
      options: shuffleArray([
        word,
        ...getAdjectivesPool(grade).slice(0, 2),
        ...getVerbsPool(grade).slice(0, 1)
      ]),
      correctAnswer: word,
    }));
  
    // IDENTIFY from sentence pool
    const sentenceQs = getSentencePool(grade).map(s => ({
      questionType: "IDENTIFY",
      question: `Identify all ${s.focus} in the sentence: "${s.sentence}"`,
      correctAnswers: s.correctAnswers,
    }));
  
    // MC main idea from reading pool
    const readingQs = getReadingPool(grade).map(r => ({
      questionType: "MC",
      question: `What is the main idea of this passage? "${r.statement}"`,
      options: shuffleArray([
        r.mainIdea,
        ...getReadingPool(grade)
          .filter(x => x !== r)
          .slice(0, 3)
          .map(x => x.mainIdea),
      ]),
      correctAnswer: r.mainIdea,
    }));
  
    // Advanced helper questions (usually 1 each)
    const helperQs = [
      buildLiteraryDeviceMC(literaryDevicesPool),
      buildTextStructureMC(textStructurePool),
      buildConnotationMC(connotationPool),
      buildSentenceTypeMC(sentenceTypesPool),
      buildMediaDeviceMC(mediaDevicesPool),
    ].filter(Boolean);
  
    // Return a large, fully-shuffled question pool
    return shuffleArray([
      ...advancedNounQs,
      ...advancedVerbQs,
      ...advancedAdjQs,
      ...advancedAdvQs,
      ...nounPool,
      ...verbPool,
      ...adjPool,
      ...advPool,
      ...sentenceQs,
      ...readingQs,
      ...helperQs
    ]);
  }
  

  function generateFinalQuiz(grade, count = 45) {
    const allQs = buildFullQuestionBank(grade);
    const n = Math.min(count, allQs.length);
    const shuffled = shuffleArray(allQs);
    return shuffled.slice(0, n);
  }

  return (
    <div className="grade3-english-page">
      <div style={{ margin: "10px 0" }}>
        <div
          style={{
            width: "80%",
            margin: "0 auto",
            background: "#ddd",
            borderRadius: 10,
            height: 20,
          }}
        >
          <div
            style={{
              width: `${progress}%`,
              background: "#4caf50",
              height: 20,
              transition: "width 0.3s",
            }}
          />
        </div>
        <p>{progress}% complete</p>
      </div>

      {hasCompletedBefore && !showQuiz && !showFinal && (
        <div style={{ margin: "15px 0", textAlign: "center" }}>
          <p>
            You previously completed all grade 9 English lessons. You can jump
            to any chapter or retake the final quiz.
          </p>
          <div>
            {[1, 2, 3, 4].map(ch => (
              <button
                key={ch}
                className="button1"
                onClick={() => {
                  setChapDone(false);
                  setChapter(ch);
                  setPageIdx(0);
                  setShowQuiz(false);
                  setShowFinal(false);
                }}
              >
                Go to Chapter {ch}
              </button>
            ))}
            <button className="button1" onClick={startFinalQuiz}>
              Go to Final Quiz
            </button>
          </div>
        </div>
      )}

      {!chapDone && !showFinal && <ChapterUI />}

      {chapDone && !showFinal && (
        <div style={{ textAlign: "center" }}>
          <h3>All Chapters Completed!</h3>
          <button className="button1" onClick={startFinalQuiz}>
            Start Final Quiz
          </button>
        </div>
      )}

      {showFinal && (
        <FinalQuiz
          cQ={finalQs[fIdx]}
          fIdx={fIdx}
          finalQs={finalQs}
          handleFinalAnswer={handleFinalAnswer}
          fScore={fScore}
          grade={9}
        />
      )}

      <br />
      <Link to="/english" className="button1">← Back to English Page</Link>
    </div>
  );
}
